import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useAuth } from 'react-oidc-context'
import { useNavigate } from '@tanstack/react-router'
import {
  hasPermission,
  type CurrentUser,
  type Permission,
  type RoleRef,
  type User,
} from '@rapid-response/shared'
import { syncCurrentUser } from '~/server/users'
import { setAccessToken } from '~/lib/access-token'

interface CurrentUserState {
  user: User | null
  // The user's role row — `{id, name, description, system}`. Used for
  // display ("Logged in as Tiago (Admin)"), not for authorization checks.
  role: RoleRef | null
  // Server-derived permission set. Source of truth for every gate. Calling
  // `hasPermission(state.permissions, p)` is equivalent to invoking the
  // `usePermission(p)` hook below — the hook just narrows the call.
  permissions: readonly Permission[] | null
  loading: boolean
  error: Error | null
  /** Re-run the sync (e.g. after an admin changes your own role). */
  refresh: () => Promise<void>
}

const CurrentUserContext = createContext<CurrentUserState | null>(null)

export function CurrentUserProvider({ children }: Readonly<{ children: ReactNode }>) {
  const auth = useAuth()
  const [current, setCurrent] = useState<CurrentUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const accessToken = auth.user?.access_token ?? null

  // Mirror the OIDC access token into the module-level holder that the RPC
  // client middleware reads at fetch time. The token rotates on silent renew
  // and we want the holder current *before* the sync useEffect fires, so
  // declare this effect first — React runs effects in declaration order on
  // mount, which guarantees the bootstrap middleware can read the token on
  // first sign-in. On signout the token goes back to `null`, which makes
  // the next RPC fail with 401 at the verifier.
  useEffect(() => {
    setAccessToken(accessToken)
    return () => setAccessToken(null)
  }, [accessToken])

  const sync = useCallback(async () => {
    if (!accessToken) return
    setLoading(true)
    setError(null)
    try {
      // Identity (id/email/name) is derived from the verified JWT server-
      // side. The body intentionally carries no identity fields. The RPC
      // returns the user row + the server-resolved permission set.
      const synced = await syncCurrentUser({ data: {} })
      setCurrent(synced)
    } catch (e) {
      setError(e as Error)
    } finally {
      setLoading(false)
    }
  }, [accessToken])

  useEffect(() => {
    if (!auth.isAuthenticated) {
      setCurrent(null)
      setLoading(false)
      return
    }
    sync()
  }, [auth.isAuthenticated, sync])

  const value = useMemo<CurrentUserState>(
    () => ({
      user: current,
      role: current?.role ?? null,
      permissions: current?.permissions ?? null,
      loading,
      error,
      refresh: sync,
    }),
    [current, loading, error, sync],
  )

  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  )
}

export function useCurrentUser(): CurrentUserState {
  const ctx = useContext(CurrentUserContext)
  if (!ctx) throw new Error('useCurrentUser must be used inside CurrentUserProvider')
  return ctx
}

/** Returns true if the current user has the given permission. */
export function usePermission(permission: Permission): boolean {
  const { permissions } = useCurrentUser()
  return hasPermission(permissions, permission)
}

/**
 * Page-level permission gate: bounces the user back to home once their
 * permission set has resolved and lacks `permission`. Returns true only when
 * the permission is held — render a "checking permissions" placeholder while
 * false. Server functions / REST routes still enforce their own checks; this
 * just keeps gated pages out of reach in the UI.
 */
export function usePermissionGuard(permission: Permission): boolean {
  const { permissions, loading } = useCurrentUser()
  const navigate = useNavigate()
  const allowed = hasPermission(permissions, permission)

  // Redirect whenever the permission isn't held once loading settles — that
  // includes a `null` permission set (no session, or the sync failed), so a
  // gated page can't sit on its placeholder forever.
  useEffect(() => {
    if (!loading && !allowed) {
      navigate({ to: '/' }).catch(() => {})
    }
  }, [loading, allowed, navigate])

  return allowed
}
